import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { Message } from 'rsuite'
import useStandardToaster from '../../../ui_reusable/StandardToaster'
import SimpleAccordion from '../../../ui_reusable/SimpleAccordion'
import VersatileGrid, { Header } from '../../../ui_reusable/VersatileGrid'
import {
  OptimizeChannel,
  OptimizeArtifactRequest,
  ChannelDriverInputRequestAdstockDecay,
} from '../../../../api/generated/models'
import { StandardCheckbox } from '../../../ui_reusable/StandardInputs'
import { FormSelectPicker } from '../../../ui_reusable/StandardSelectPickers'
import { HeaderCellFont } from '../../../ui_reusable/styles/VersatileGrid.styles'
import { StatsContainer } from '../../../ui_reusable/styles/VersatileGridUtils.styles'
import { HeaderMedium600Style } from '../../../../styles/Text.styles'
import { SmallNormalTag } from '../../../ui_reusable/styles/Tags.styles'
import {
  getQualityTagByQualityString,
  QualityString,
} from '../../../../utils/ScoreFitnessValidation'
import { GridCellBoldText } from '../../../ui_reusable/styles/VersatileGridUtils.styles'
import {
  DriverNameContainer,
  ConditionalGridCellNormalText,
  GridCellBoldTextInvalid,
} from './styles/BuildModelDriverList.styles'
import { Tooltip, Whisper } from 'rsuite'
import theme from '../../../../styles/theme'
import DriverSelectionFilter from '../../../comp_reusable/lists/DriverSelectionFilter'

interface ChannelFormData {
  channel_id: number
  spend_dataset_id: number | null
  impression_dataset_id: number | null
  reach_dataset_id: number | null
  frequency_dataset_id: number | null
  adstock_decay?: ChannelDriverInputRequestAdstockDecay
  roi_assumption?: string
}

interface FlattenedChannel {
  id: number
  name: string
  is_active: boolean
  comment: string | null
  regions: string[]
  regionCount: number
  spendDrivers: Array<{ id: number; name: string }>
  impressionDrivers: Array<{ id: number; name: string }>
  reachFreqPairs: Array<{
    id: string
    name: string
    reachId: number
    frequencyId: number
  }>
  quality: string
  updated_at: string
  _searchId: string
  _originalIndex: number
}

const SelectAllCheckbox = React.memo(
  React.forwardRef<
    HTMLDivElement,
    { allSelected: boolean; onSelectAll: () => void }
  >(({ allSelected, onSelectAll }, ref) => {
    return (
      <div
        ref={ref}
        style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
      >
        <StandardCheckbox onChange={onSelectAll} checked={allSelected} />
        <span css={HeaderCellFont}>Select all/none</span>
      </div>
    )
  }),
)

const ChannelsForm = ({ channels }: { channels: OptimizeChannel[] }) => {
  const methods = useFormContext<OptimizeArtifactRequest>()
  const { pushToast } = useStandardToaster()
  const [selectedChannelIds, setSelectedChannelIds] = useState<number[]>([])
  const [channelConfigs, setChannelConfigs] = useState<
    Map<number, ChannelFormData>
  >(new Map())
  const selectAllCheckboxRef = useRef<HTMLDivElement>(null)
  const filteredChannelIdsRef = useRef(new Set<number>())
  const [showSelectedOnly, setShowSelectedOnly] = useState(false)
  const [availableFirst, setAvailableFirst] = useState(false)

  // Initialize from form values when artifact is loaded
  useEffect(() => {
    const formChannelInputs = methods.getValues('channel_driver_inputs')
    if (
      formChannelInputs &&
      formChannelInputs.length > 0 &&
      selectedChannelIds.length === 0
    ) {
      const channelIds = formChannelInputs
        .map((input: any) => input.channel_id)
        .filter(Boolean)
      setSelectedChannelIds(channelIds)

      const configMap = new Map<number, ChannelFormData>()
      formChannelInputs.forEach((input: any) => {
        if (input.channel_id) {
          configMap.set(input.channel_id, {
            channel_id: input.channel_id,
            spend_dataset_id: input.spend_dataset_id ?? null,
            impression_dataset_id: input.impression_dataset_id ?? null,
            reach_dataset_id: input.reach_dataset_id ?? null,
            frequency_dataset_id: input.frequency_dataset_id ?? null,
            adstock_decay: input.adstock_decay,
          })
        }
      })
      setChannelConfigs(configMap)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Flatten channel data for the grid
  const flattenedData: FlattenedChannel[] = useMemo(() => {
    // Helper: indent every line of backend content with the given prefix
    // This ensures multi-line backend content stays properly indented
    const indentContent = (content: string, indent: string): string => {
      return content
        .split('\n')
        .map((line) => `${indent}${line}`)
        .join('\n')
    }

    return channels.map((channel, index) => {
      // Extract unique regions from all drivers
      const regions = new Set<string>()

      // Build structured comments
      // Frontend adds structure (headers, indentation, bullets)
      // Backend content gets indented to match its context level
      const commentSections: string[] = []

      // Channel-level comment (content indented once)
      if (channel.comment?.trim()) {
        commentSections.push(`Channel:\n${indentContent(channel.comment.trim(), '\t')}`)
      }

      // Driver sections
      const driverSections: string[] = []

      // Spend driver comments (category indented once, driver content indented twice with bullet on first line)
      const spendComments: string[] = []
      channel.spend_drivers?.forEach((driver: any) => {
        if (driver.driver?.comment?.trim()) {
          // Indent all backend content lines with 3 tabs (under the driver name)
          const indentedComment = indentContent(driver.driver.comment.trim(), '\t\t\t')
          // Driver name with bullet, then the indented content
          spendComments.push(`\t\t• ${driver.driver.name}:\n${indentedComment}`)
        }
        driver.driver?.regional_variants?.forEach((variant: any) => {
          if (variant.region?.name) regions.add(variant.region.name)
        })
      })
      if (spendComments.length > 0) {
        driverSections.push(`\tSpend drivers:\n${spendComments.join('\n')}`)
      }

      // Impression driver comments
      const impressionComments: string[] = []
      channel.impression_drivers?.forEach((driver: any) => {
        if (driver.driver?.comment?.trim()) {
          const indentedComment = indentContent(driver.driver.comment.trim(), '\t\t\t')
          impressionComments.push(`\t\t• ${driver.driver.name}:\n${indentedComment}`)
        }
        driver.driver?.regional_variants?.forEach((variant: any) => {
          if (variant.region?.name) regions.add(variant.region.name)
        })
      })
      if (impressionComments.length > 0) {
        driverSections.push(`\tImpression drivers:\n${impressionComments.join('\n')}`)
      }

      // Reach/frequency pair comments
      const reachFreqComments: string[] = []
      channel.reach_frequency_pairs?.forEach((pair: any) => {
        if (pair.comment?.trim()) {
          const pairName = `${pair.reach?.driver?.name || 'Unknown'} + ${pair.frequency?.driver?.name || 'Unknown'}`
          const indentedComment = indentContent(pair.comment.trim(), '\t\t\t')
          reachFreqComments.push(`\t\t• ${pairName}:\n${indentedComment}`)
        }
        pair.reach?.regional_variants?.forEach((variant: any) => {
          if (variant.region?.name) regions.add(variant.region.name)
        })
      })
      if (reachFreqComments.length > 0) {
        driverSections.push(`\tReach/Frequency pairs:\n${reachFreqComments.join('\n')}`)
      }

      // Add Drivers section if there are any driver comments
      if (driverSections.length > 0) {
        commentSections.push(`Drivers:\n${driverSections.join('\n\n')}`)
      }

      const regionArray = Array.from(regions)

      // Extract spend drivers (filter out inactive drivers)
      const spendDrivers = (channel.spend_drivers || [])
        .filter((driver: any) => driver.driver?.is_active !== false)
        .map((driver: any) => ({
          id: driver.driver?.id || 0,
          name: driver.driver?.name || 'Unknown',
        }))

      // Extract impression drivers (filter out inactive drivers)
      const impressionDrivers = (channel.impression_drivers || [])
        .filter((driver: any) => driver.driver?.is_active !== false)
        .map((driver: any) => ({
          id: driver.driver?.id || 0,
          name: driver.driver?.name || 'Unknown',
        }))

      // Extract reach-frequency pairs (filter out inactive drivers)
      const reachFreqPairs = (channel.reach_frequency_pairs || [])
        .filter(
          (pair: any) =>
            pair.reach?.driver?.is_active !== false &&
            pair.frequency?.driver?.is_active !== false,
        )
        .map((pair: any, idx: number) => ({
          id: `pair_${channel.id}_${idx}`,
          name: `${pair.reach?.driver?.name || 'Unknown'} + ${pair.frequency?.driver?.name || 'Unknown'}`,
          reachId: pair.reach?.driver?.id || 0,
          frequencyId: pair.frequency?.driver?.id || 0,
        }))

      // Determine quality based on channel data
      const quality: QualityString = channel.is_active ? 'good' : 'check'

      // Join sections with double newlines for clear separation
      const combinedComments = commentSections.join('\n\n')

      return {
        id: channel.id,
        name: channel.name,
        is_active: channel.is_active,
        comment: combinedComments,
        regions: regionArray,
        regionCount: regionArray.length,
        spendDrivers,
        impressionDrivers,
        reachFreqPairs,
        quality,
        updated_at: channel.updated_at,
        _searchId: `channel-${channel.id}`,
        _originalIndex: index,
      }
    })
  }, [channels])

  const visibleData = useMemo(() => {
    const rows = showSelectedOnly
      ? flattenedData.filter((channel) =>
          selectedChannelIds.includes(channel.id),
        )
      : flattenedData
    if (!availableFirst) return rows
    return [...rows].sort(
      (a, b) => Number(a.is_active === false) - Number(b.is_active === false),
    )
  }, [flattenedData, showSelectedOnly, selectedChannelIds, availableFirst])

  // Automatically deselect channels that become inactive
  useEffect(() => {
    const inactiveChannelIds = new Set(
      flattenedData
        .filter((channel) => !channel.is_active)
        .map((channel) => channel.id),
    )

    const invalidSelectedIds = selectedChannelIds.filter((id) =>
      inactiveChannelIds.has(id),
    )

    if (invalidSelectedIds.length > 0) {
      const newSelection = selectedChannelIds.filter(
        (id) => !inactiveChannelIds.has(id),
      )
      setSelectedChannelIds(newSelection)

      // Notify the user about deselected channels
      const deselectedChannelNames = invalidSelectedIds
        .map((id) => {
          const channel = flattenedData.find((c) => c.id === id)
          return channel?.name || id
        })
        .join(', ')

      pushToast(
        <Message type="warning" style={{ maxWidth: '25rem' }}>
          The following channels are no longer available and have been
          deselected: {deselectedChannelNames}
        </Message>,
        { placement: 'topCenter', duration: 10000 },
      )
    }
  }, [flattenedData, selectedChannelIds, pushToast])

  const handleCheckboxClick = (channelId: number) => {
    const updatedSelection = selectedChannelIds.includes(channelId)
      ? selectedChannelIds.filter((id) => id !== channelId)
      : [...selectedChannelIds, channelId]
    setSelectedChannelIds(updatedSelection)
  }

  const getSelectableChannels = useCallback(() => {
    return visibleData.filter((c) => c.is_active !== false)
  }, [visibleData])

  const getFilteredSelectableChannels = useCallback(() => {
    if (filteredChannelIdsRef.current.size === 0) return getSelectableChannels()
    return getSelectableChannels().filter((channel) =>
      filteredChannelIdsRef.current.has(channel.id),
    )
  }, [getSelectableChannels])

  const handleFilteredDataChange = useCallback(
    (filteredData: any[]) => {
      filteredChannelIdsRef.current = new Set(
        filteredData.map((item) => item.id),
      )
      const filteredSelectableChannels = getFilteredSelectableChannels()
      const newAllFilteredSelected =
        filteredSelectableChannels.length > 0 &&
        filteredSelectableChannels.every((channel) =>
          selectedChannelIds.includes(channel.id),
        )
      if (selectAllCheckboxRef.current) {
        const checkbox = selectAllCheckboxRef.current.querySelector(
          'input[type="checkbox"]',
        ) as HTMLInputElement | null
        if (checkbox) {
          checkbox.checked = newAllFilteredSelected
        }
      }
    },
    [getFilteredSelectableChannels, selectedChannelIds],
  )

  const handleSelectAll = () => {
    const filteredSelectableChannels = getFilteredSelectableChannels()
    const allSelected =
      filteredSelectableChannels.length > 0 &&
      filteredSelectableChannels.every((channel) =>
        selectedChannelIds.includes(channel.id),
      )

    let updatedSelection
    if (allSelected) {
      updatedSelection = selectedChannelIds.filter(
        (id) =>
          !filteredSelectableChannels.some((channel) => channel.id === id),
      )
    } else {
      const existingSelections = new Set(selectedChannelIds)
      filteredSelectableChannels.forEach((channel) => {
        existingSelections.add(channel.id)
      })
      updatedSelection = Array.from(existingSelections)
    }
    setSelectedChannelIds(updatedSelection)
  }

  const allFilteredSelected = (() => {
    const filteredSelectableChannels = getFilteredSelectableChannels()
    return (
      filteredSelectableChannels.length > 0 &&
      filteredSelectableChannels.every((channel) =>
        selectedChannelIds.includes(channel.id),
      )
    )
  })()

  // Update form values when channel configs change
  useEffect(() => {
    const channelDrivers = selectedChannelIds.map((channelId) => {
      const config = channelConfigs.get(channelId)
      return {
        channel_id: channelId,
        spend_dataset_id: config?.spend_dataset_id || null,
        impression_dataset_id: config?.impression_dataset_id || null,
        reach_dataset_id: config?.reach_dataset_id || null,
        frequency_dataset_id: config?.frequency_dataset_id || null,
        adstock_decay: config?.adstock_decay ?? 'auto',
      }
    })
    methods.setValue('channel_driver_inputs', channelDrivers)
  }, [selectedChannelIds, channelConfigs, methods])

  const handleSpendChange = (
    channelId: number,
    value: string | number | null,
  ) => {
    setChannelConfigs((prev) => {
      const newMap = new Map(prev)
      const current = newMap.get(channelId) || {
        channel_id: channelId,
        spend_dataset_id: null,
        impression_dataset_id: null,
        reach_dataset_id: null,
        frequency_dataset_id: null,
        adstock_decay: 'auto' as ChannelDriverInputRequestAdstockDecay,
      }
      newMap.set(channelId, {
        ...current,
        spend_dataset_id: value === null ? null : Number(value),
      })
      return newMap
    })
  }

  const handleMeasureChange = (
    channelId: number,
    value: string | number | null,
  ) => {
    setChannelConfigs((prev) => {
      const newMap = new Map(prev)
      const current = newMap.get(channelId) || {
        channel_id: channelId,
        spend_dataset_id: null,
        impression_dataset_id: null,
        reach_dataset_id: null,
        frequency_dataset_id: null,
        adstock_decay: 'auto' as ChannelDriverInputRequestAdstockDecay,
      }

      // Parse the value - could be impression ID or "pair_X_Y"
      if (value === null) {
        newMap.set(channelId, {
          ...current,
          impression_dataset_id: null,
          reach_dataset_id: null,
          frequency_dataset_id: null,
        })
      } else if (String(value).startsWith('pair_')) {
        // It's a reach-frequency pair
        const channel = flattenedData.find((c) => c.id === channelId)
        const pair = channel?.reachFreqPairs.find((p) => p.id === value)
        if (pair) {
          newMap.set(channelId, {
            ...current,
            impression_dataset_id: null,
            reach_dataset_id: pair.reachId,
            frequency_dataset_id: pair.frequencyId,
          })
        }
      } else {
        // It's an impression
        newMap.set(channelId, {
          ...current,
          impression_dataset_id: Number(value),
          reach_dataset_id: null,
          frequency_dataset_id: null,
        })
      }
      return newMap
    })
  }

  const handleDecayRateChange = (
    channelId: number,
    value: string | number | null,
  ) => {
    setChannelConfigs((prev) => {
      const newMap = new Map(prev)
      const current = newMap.get(channelId) || {
        channel_id: channelId,
        spend_dataset_id: null,
        impression_dataset_id: null,
        reach_dataset_id: null,
        frequency_dataset_id: null,
        adstock_decay: 'auto' as ChannelDriverInputRequestAdstockDecay,
      }
      newMap.set(channelId, {
        ...current,
        adstock_decay: value as ChannelDriverInputRequestAdstockDecay | undefined,
      })
      return newMap
    })
  }

  const headers: Header[] = [
    {
      title: 'Channel Name',
      key: 'name',
      type: 'string',
      isSortable: true,
      isSearchable: true,
      overrideJSX: (
        <SelectAllCheckbox
          ref={selectAllCheckboxRef}
          allSelected={allFilteredSelected}
          onSelectAll={handleSelectAll}
        />
      ),
    },
    {
      title: 'Segment',
      key: 'regionCount',
      type: 'number',
      isSortable: true,
      isSearchable: false,
      center: true,
    },
    {
      title: 'Last updated',
      key: 'updated_at',
      type: 'datetime',
      isSortable: true,
      isSearchable: false,
      center: true,
    },
    {
      title: 'Spend driver',
      key: 'spend',
      type: 'string',
      isSortable: false,
      isSearchable: false,
      cellKeeperStyle: { overflow: 'visible', minWidth: 0 },
      cellContentStyle: { overflow: 'visible', width: '100%', minWidth: 0 },
      cellStyle: { minWidth: 0 },
      style: { textAlign: 'start', minWidth: 0 },
    },
    {
      title: 'Exposure variable',
      key: 'measure',
      type: 'string',
      isSortable: false,
      isSearchable: false,
      cellKeeperStyle: { overflow: 'visible', minWidth: 0 },
      cellContentStyle: { overflow: 'visible', width: '100%', minWidth: 0 },
      cellStyle: { minWidth: 0 },
      style: { textAlign: 'start', minWidth: 0 },
    },
    {
      title: 'Decay reate',
      key: 'adstock_decay',
      type: 'string',
      isSortable: false,
      isSearchable: false,
      cellKeeperStyle: { overflow: 'visible', minWidth: 0 },
      cellContentStyle: { overflow: 'visible', width: '100%', minWidth: 0 },
      cellStyle: { minWidth: 0 },
      style: { textAlign: 'start', minWidth: 0 },
    },
    {
      title: 'Quality',
      key: 'quality',
      type: 'number',
      isSortable: true,
      isSearchable: false,
      center: true,
      style: { flexGrow: 0.8 },
      getSortValue: (row: FlattenedChannel) => (row.is_active ? 0 : 1),
    },
  ]

  const gridHeader = (
    <StatsContainer>
      <span css={HeaderMedium600Style}>
        Available Channels: {flattenedData.length}
      </span>
      <div>
        <SmallNormalTag>
          {selectedChannelIds.length} channels selected
        </SmallNormalTag>
      </div>
      <DriverSelectionFilter
        showSelectedOnly={showSelectedOnly}
        onChange={setShowSelectedOnly}
        availableFirst={availableFirst}
        onAvailableFirstChange={setAvailableFirst}
      />
    </StatsContainer>
  )

  const renderCheckbox = (
    channelId: number,
    isDisabled: boolean,
    tooltipText: string,
    isChecked: boolean,
  ) => {
    if (isDisabled) {
      return (
        <Whisper
          placement="topStart"
          trigger="hover"
          speaker={
            <Tooltip
              style={{
                backgroundColor: theme.colors.background,
                color: theme.colors.veryVeryFadedText,
                outline: '1px solid',
                width: 'auto',
                maxWidth: 'none',
              }}
            >
              {tooltipText.split('\n').map((line, index) => (
                <div key={index} style={{ whiteSpace: 'pre-wrap' }}>
                  {line}
                </div>
              ))}
            </Tooltip>
          }
        >
          <span>
            <StandardCheckbox
              onChange={() => {}}
              disabled={true}
              checked={isChecked}
            />
          </span>
        </Whisper>
      )
    }

    return (
      <StandardCheckbox
        onChange={() => handleCheckboxClick(channelId)}
        disabled={false}
        checked={isChecked}
      />
    )
  }

  return (
    <SimpleAccordion.Panel header="Select channels" eventKey={'3'}>
      <VersatileGrid
        headers={headers}
        data={visibleData}
        searchPlaceholder="Find channel"
        renderRow={(rowData: any, rowIdx: number, dataIdx: number) => {
          const typedRowData = rowData as FlattenedChannel
          const isDisabled = !typedRowData.is_active
          const tooltipText =
            typedRowData.comment || 'This channel is not available'
          const config = channelConfigs.get(typedRowData.id)

          // Build spend driver options
          const spendOptions = [
            { label: 'None', value: null },
            ...typedRowData.spendDrivers.map((driver) => ({
              label: driver.name,
              value: driver.id,
            })),
          ]

          // Build measure driver options (impressions + reach-freq pairs)
          const measureOptions = [
            { label: 'None', value: null },
            ...typedRowData.impressionDrivers.map((driver) => ({
              label: driver.name,
              value: driver.id,
            })),
            ...typedRowData.reachFreqPairs.map((pair) => ({
              label: pair.name,
              value: pair.id,
            })),
          ]

          // Determine current measure value
          const currentMeasureValue = config?.impression_dataset_id
            ? config.impression_dataset_id
            : config?.reach_dataset_id && config?.frequency_dataset_id
              ? typedRowData.reachFreqPairs.find(
                  (p) =>
                    p.reachId === config.reach_dataset_id &&
                    p.frequencyId === config.frequency_dataset_id,
                )?.id || null
              : null

          return [
            <DriverNameContainer title={typedRowData.name}>
              {renderCheckbox(
                typedRowData.id,
                isDisabled,
                tooltipText,
                selectedChannelIds.includes(typedRowData.id),
              )}
              {isDisabled ? (
                <GridCellBoldTextInvalid>
                  {typedRowData.name}
                </GridCellBoldTextInvalid>
              ) : (
                <GridCellBoldText>{typedRowData.name}</GridCellBoldText>
              )}
            </DriverNameContainer>,
            <ConditionalGridCellNormalText isInvalid={isDisabled}>
              {typedRowData.regionCount}
            </ConditionalGridCellNormalText>,
            <ConditionalGridCellNormalText isInvalid={isDisabled}>
              {typedRowData.updated_at
                ? `${new Date(rowData.updated_at).toISOString().slice(0, 10)}`
                : ''}
            </ConditionalGridCellNormalText>,
            <div style={{ padding: '0.25rem 0', width: '100%', minWidth: 0 }}>
              <FormSelectPicker
                data={spendOptions}
                value={config?.spend_dataset_id || null}
                onChange={(value) => handleSpendChange(typedRowData.id, value)}
                placeholder="Select spend"
                style={{ width: '100%', minWidth: 0, maxWidth: '100%' }}
                disabled={!selectedChannelIds.includes(typedRowData.id)}
              />
            </div>,
            <div style={{ padding: '0.25rem 0', width: '100%', minWidth: 0 }}>
              <FormSelectPicker
                data={measureOptions}
                value={currentMeasureValue}
                onChange={(value) =>
                  handleMeasureChange(typedRowData.id, value)
                }
                placeholder="Select measure"
                style={{ width: '100%', minWidth: 0, maxWidth: '100%' }}
                disabled={!selectedChannelIds.includes(typedRowData.id)}
              />
            </div>,
            <div style={{ padding: '0.25rem 0', width: '100%', minWidth: 0 }}>
              <FormSelectPicker
                data={[
                  { label: 'Auto', value: 'auto' },
                  { label: 'Tactical', value: 'tactical' },
                  { label: 'Gradual Build', value: 'gradual_build' },
                  { label: 'Sustained', value: 'sustained' },
                  { label: 'Pulse and fade', value: 'pulse_and_fade' },
                ]}
                value={config?.adstock_decay ?? 'auto'}
                onChange={(value) =>
                  handleDecayRateChange(typedRowData.id, value)
                }
                placeholder="Select decay rate"
                style={{ width: '100%', minWidth: 0, maxWidth: '100%' }}
                disabled={!selectedChannelIds.includes(typedRowData.id)}
              />
            </div>,
            <ConditionalGridCellNormalText isInvalid={isDisabled}>
              {getQualityTagByQualityString(
                typedRowData.quality as QualityString,
              )}
            </ConditionalGridCellNormalText>,
          ]
        }}
        maxDisplayedItems={8}
        headerInfo={gridHeader}
        gridContainerStyle={{
          gridTemplateColumns:
            'minmax(10rem, 1.1fr) minmax(5.5rem, 0.5fr) minmax(6.5rem, 0.65fr) minmax(0, 1.5fr) minmax(0, 1.5fr) minmax(7rem, 0.75fr) minmax(6.5rem, 0.55fr)',
        }}
        onFilteredDataChange={handleFilteredDataChange}
      />
    </SimpleAccordion.Panel>
  )
}

export default ChannelsForm
